import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Input, ComponentEventHandler, RadioGroup, RadioGroupItemProps, Text, ICSSInJSStyle } from '@fluentui/react-northstar';
import { EyeIcon, EyeSlashIcon } from '@fluentui/react-icons-northstar';
import ErrorAlert from '../errorAlert/ErrorAlert';
import HopexApi from '../../api/HopexApi';
import AppContext from '../../contexts/AppContext';
import { IHopexCredentials } from '../../models/IHopexCredentials';
import { IHopexEnvironment } from '../../models/IHopexEnvironment';

const urlparams = new URLSearchParams(document.location.search);

interface LoginProps {
    isInPopin: boolean;
    defaultCredentials?: IHopexCredentials;
}

// styles
const styles: { [key: string]: ICSSInJSStyle } = {
};

const Login = (props: LoginProps) => {
    const { isInPopin, defaultCredentials } = props;
    const { t } = useTranslation();
    const { setHopexContext } = useContext(AppContext);

    let canUseStorage = true;
    try {
        localStorage.getItem('test');
    } catch {
        canUseStorage = false;
    }

    const [canChangeServerUrl, setCanChangeServerUrl] = useState<boolean>(!urlparams.get('serverUrl'));
    const [serverUrl, setServerUrl] = useState<string | undefined>(urlparams.get('serverUrl') || undefined);
    const [authenticationMethod, setAuthenticationMethod] = useState<'user' | 'api'>('user');
    const [apiKey, setApiKey] = useState<string | undefined>();
    const [username, setUsername] = useState<string | undefined>();
    const [password, setPassword] = useState<string | undefined>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [authenticating, setAuthenticating] = useState<boolean>(false);
    const [newCredentials, setNewCredentials] = useState<IHopexCredentials>();
    const [newHopexEnvironment, setNewHopexEnvironment] = useState<IHopexEnvironment>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [showApiKey, setShowApiKey] = useState<boolean>(false);
    const [showUserPassword, setShowUserPassword] = useState<boolean>(false);

    useEffect(() => {
        if (defaultCredentials) {            
            if (defaultCredentials.serverUrl) {
                setServerUrl(defaultCredentials.serverUrl);
                setCanChangeServerUrl(false);
            }
            if (defaultCredentials.authenticationMethod) setAuthenticationMethod(defaultCredentials.authenticationMethod);
            setApiKey(defaultCredentials.apiKey || '');
            setUsername(defaultCredentials.username || '');
            setPassword(defaultCredentials.password || '');
        }
    }, [defaultCredentials]);

    const handleLogin = () => {
        if (!authenticating && serverUrl && 
            ((authenticationMethod === 'api' && !!apiKey) || (authenticationMethod === 'user' && !!username && !!password))
        ) {
            setAuthenticating(true);
            setNewCredentials(undefined);
            setNewHopexEnvironment(undefined);
            const creds = {
                serverUrl,
                authenticationMethod,
                apiKey,
                username,
                password,
            } as IHopexCredentials;
            HopexApi.getContext(creds)
                .then((result) => {
                    if (result.data.data._currentContext) {
                        if (canUseStorage) {
                            localStorage.setItem('serverUrl', serverUrl || '');
                        }
                        setIsAuthenticated(true);
                        setHopexContext({
                            credentials: creds,
                            environment: result.data.data._currentContext,
                        });
                    }
                    else {
                        // Error ?
                    }
                }).catch((reason) => {
                    console.log(reason);
                    if (reason?.response?.status === 401 || reason?.response?.status === 403) {
                        setErrorMessage(t('Error.BadCredentials'))
                    } else {
                        setErrorMessage(t('Error.CannotLogin'))
                    }
                }).finally(() => {
                    setAuthenticating(false);
                });
        }
    };

    useEffect(() => {
        if (isAuthenticated && newCredentials && newHopexEnvironment) {

        }
    }, [isAuthenticated, newCredentials, newHopexEnvironment]);

    const handleAuthenticationMethodChange: ComponentEventHandler<RadioGroupItemProps> = (event, data) => {
        setAuthenticationMethod(data?.value === 'user' ? 'user' : 'api');
    };

    return (<Flex column gap={"gap.medium"} className="login-form">
            <Text content={ defaultCredentials ? t("Configure.LoginFirstReconfig") : t("Configure.LoginFirst")} styles={{ marginBottom: '0 !important' }} />
            <Text content={t("Configure.LoginSection")} as="h3" />
            <Input
                name='server-url'
                key='serverUrl'
                fluid
                className='login-input'
                clearable={!isInPopin}
                required
                label={t('Configure.ServerUrl')}
                disabled={authenticating || !canChangeServerUrl}
                value={serverUrl}
                styles={styles.input}
                onChange={(ev, data) => {
                    setServerUrl((data && data.value)
                        ? data.value
                        : undefined);
                }}></Input>
            <Text content={t("Configure.AuthenticationMethod")} styles={{ marginBottom: '3px !important' }} />
            <RadioGroup
                key={'authenticationMethod'}
                onCheckedValueChange={handleAuthenticationMethodChange}
                checkedValue={authenticationMethod}
                items={[
                    {
                        key: 'user',
                        label: t('Configure.AuthenticationMethodUser'),
                        value: 'user',
                    },
                    {
                        key: 'api',
                        label: t('Configure.AuthenticationMethodApiKey'),
                        value: 'api',
                    },
                ]}
            />
            {
                authenticationMethod === 'user' && (
                    <Flex gap={"gap.large"}>
                        <Input
                            name='username'
                            key='username'
                            fluid
                            className='login-input'
                            label={t('Configure.Username')}
                            clearable
                            required
                            disabled={authenticating}
                            hidden={(isAuthenticated && !!apiKey)}
                            value={username}
                            styles={styles.input}
                            onChange={(ev, data) => {
                                setUsername((data && data.value)
                                    ? data.value
                                    : undefined);
                            }}></Input>
                        <Input
                            name='password'
                            key='password'
                            fluid
                            showSuccessIndicator={false}
                            className='login-input'
                            icon={showUserPassword ? <EyeSlashIcon onClick={() => setShowUserPassword(false)} /> : <EyeIcon onClick={() => setShowUserPassword(true)} />}
                            iconPosition="end"
                            type={showUserPassword ? "text" : "password"}
                            disabled={authenticating}
                            hidden={(isAuthenticated && !!apiKey)}
                            label={t('Configure.Password')}
                            required
                            value={password}
                            styles={styles.input}
                            onChange={(ev, data) => {
                                setPassword((data && data.value)
                                    ? data.value
                                    : undefined);
                            }}></Input>
                    </Flex>
                )
            }
            {
                authenticationMethod === 'api' && (
                    <Input
                        name='apikey'
                        key='apikey'
                        fluid
                        showSuccessIndicator={false}
                        className='login-input'
                        disabled={authenticating}
                        hidden={(isAuthenticated && !apiKey)}
                        label={t('Configure.ApiKey')}
                        required
                        icon={showApiKey ? <EyeSlashIcon onClick={() => setShowApiKey(false)} /> : <EyeIcon onClick={() => setShowApiKey(true)} />}
                        iconPosition="end"
                        type={showApiKey ? "text" : "password"}
                        value={apiKey}
                        styles={styles.input}
                        onChange={(ev, data) => {
                            setApiKey((data && data.value)
                                ? data.value
                                : undefined);
                        }}></Input>)
            }
            { errorMessage && (<ErrorAlert message={errorMessage} compact />) }
            <Flex hAlign="end">
                <Button
                    inverted={isInPopin}
                    primary
                    content={t('Configure.Login')}
                    onClick={handleLogin}
                    hidden={isAuthenticated}
                    loading={authenticating}
                    disabled={authenticating || !(serverUrl && (apiKey || (username && password)))}
                />
            </Flex>
            <div dangerouslySetInnerHTML={{ __html: t("Configure.NotYetCustomer") }} />
        </Flex>);
};

export default Login;